import React, { useRef, useState, useContext, useEffect } from "react";
import axios from "axios";

import styled from "@emotion/styled";
import Dialog from '@mui/material/Dialog';
import Typography from "@mui/material/Typography";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';

// components
import CustomButton from "../Common/CustomButton";
import CustomSwiper from "../Swiper/CustomSwiper";
// import Loader from "../Common/Loader";

import { addAttachment } from "../../apis/taskManagement";

import { AlertContext } from "../../contexts/AlertContext";
import { UploadContext } from "../../contexts/UploadContext";

const Container = styled("div")(({ theme }) => ({
  width: "100%",
  "& .heading": {
    padding: "0.5rem",
    // borderBottom: "1px solid #EAEAEA",
    "& .text_style": {
      fontWeight: "600",
      fontSize: "18px",
    },
  },
  "& .platform_tabs": {
    margin: "0 0.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    borderBottom: "1px solid #E5E5E5",
    gap: "0.5rem",
    "& .tab": {
      "& .tab_btn": {
        paddingBottom: 0,
        paddingRight: "2rem",
        borderRadius: 0,
        color: "#1B1C1E",
        fontWeight: "400",
      },
      "& .actv_tab_btn": {
        borderBottom: "2px solid #00A0E3",
        borderRadius: 0,
        fontWeight: "600",
      },
    },
  },
  "& .progress_container": {
    width: "100%",
    padding: "0.7rem 0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  "& .main": {
    padding: "1rem 0.5rem",
    "& .file_type": {
      display: "flex",
      alignItems: "center",
      marginBottom: "0.5rem",
      gap: "1rem",
    },
    "& .radio_name": {
      fontWeight: "400",
      fontSize: "16px",
      width: "15%",
      [theme.breakpoints.down('xl')]: {
        fontSize: "14px",
      }
    },
    "& .radio_group": {
      width: "85%",
      "& .MuiFormControlLabel-root": {
        marginRight: "1.5rem",
      },
      "& .MuiTypography-root": {
        fontSize: "16px",
        [theme.breakpoints.down('xl')]: {
          fontSize: "14px",
        }
      },
      "& .MuiRadio-root": {
        padding: "0.5rem 0.4rem",
        "& svg": {
          fontSize: "1.4rem",
          [theme.breakpoints.down('xl')]: {
            fontSize: "1.2rem",
          },
        },
      },
    },
    "& .files": {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      "& .file_preview": {
        overflow: "hidden",
        width: "100%",
        "& .image_container": {
          height: "210px",
          width: "250px",
          "& .img_style": {
            height: "200px",
            width: "250px",
            objectFit: "cover",
            borderRadius: "4px",
          },
        },
        "& .video_container": {
          width: "350px",
          height: "300px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "0.5rem",
          "& .video_block": {
            width: "100%",
            height: "100%",
            objectFit: "contain",
          },
        },
      },
      "& .button_style": {
        background: "#253A6A",
      },
    },
    "& .platform_check": {
      padding: "0.5rem 0 0 0",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "0.5rem",
      "& .MuiCheckbox-root": {
        padding: 0,
      },
      "& .MuiFormControlLabel-label": {
        fontSize: "14px",
      },
      "& svg": {
        width: "18px",
        height: "18px",
      }
    },
  },
  "& .bottom_container": {
    borderTop: "1px solid #EAEAEA",
    padding: "0.5rem",
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.5rem",
    "& .btn_styl": {
      borderRadius: "10px",
      background: "#253A6A",
    },
    "& .btn_styl_cancel": {
      borderRadius: "10px",
      color: "#787575",
      // background: "#253A6A",
    }
  }
}));

function CircularProgressWithLabel(props) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" component="div" color="text.secondary">
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const displayNameMapping = {
  "image": "Image",
  "video": "Video",
  "audio": "Audio"
};

const initialState = {
  "facebook": true,
  "instagram": true,
  "youtube": true,
};
const FileAttachmentsDialog = (props) => {
  const { onClose, selectedValue, open, taskId, getAttachmentsData } = props;
  const { showAlert } = useContext(AlertContext);
  const {uploadFiles} = useContext(UploadContext);
  const fileInputRef = useRef(null);
  const [value, setValue] = useState('image');
  const [platform, setPlatform] = useState("facebook");
  const [checkedPlatform, setCheckedPlatform] = useState({ ...initialState });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [displayFiles, setDisplayFiles] = useState([]);

  const handleChange = (event) => {
    setValue(event.target.value);
    setSelectedFiles([]);
    setDisplayFiles([]);
    if (fileInputRef?.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleClose = () => {
    onClose(selectedValue);
  };

  const handlePlatformChange = (value) => {
    setPlatform(value);
    setCheckedPlatform({ ...initialState });
  }

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Store the files
      setSelectedFiles(files);
      // Create and store preview URLs
      const fileUrls = files.map(file => URL.createObjectURL(file));
      setDisplayFiles(fileUrls);
    }
  };
  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      showAlert("Please select at least one file", "warning");
      return;
    }
    // Use the uploadFile function which now handles multiple files
    uploadFiles(selectedFiles, {
      taskId,
      platform,
      fileType: value,
      checkedPlatform,
    });
    // Reset the component state
    setSelectedFiles([]);
    setDisplayFiles([]);
    if (fileInputRef?.current) {
      fileInputRef.current.value = null;
    }
    setTimeout(() => {
      getAttachmentsData(taskId);
    }, 10000);
    handleClose();
  };

  const handlePlatformUpdate = (e, platform) => {
    setCheckedPlatform(prevState => ({
      ...prevState,
      [platform]: e.target.checked,
    }));
  };

  useEffect(() => {
    setValue("image");
    setSelectedFiles([]);
    setDisplayFiles([]);
    if (fileInputRef?.current) {
      fileInputRef.current.value = null;
    }
  }, [props]);

  useEffect(() => {
    if (open) {
      setCheckedPlatform({ ...initialState });
    }
  }, [open]);

  return (
    <Dialog onClose={handleClose} open={open} fullWidth maxWidth={"md"}>
      <Container>
        <div className="heading">
          <Typography className="text_style">Attachments</Typography>
        </div>
        <div className="platform_tabs">
          <div className="tab">
            <CustomButton
              className={
                platform === "facebook" ? "tab_btn actv_tab_btn" : "tab_btn"
              }
              onClick={() => handlePlatformChange("facebook")}
            >
              Facebook
            </CustomButton>
          </div>
          <div className="tab">
            <CustomButton
              className={
                platform === "instagram" ? "tab_btn actv_tab_btn" : "tab_btn"
              }
              onClick={() => handlePlatformChange("instagram")}
            >
              Instagram
            </CustomButton>
          </div>
          <div className="tab">
            <CustomButton
              className={
                platform === "youtube" ? "tab_btn actv_tab_btn" : "tab_btn"
              }
              onClick={() => handlePlatformChange("youtube")}
            >
              YouTube
            </CustomButton>
          </div>
        </div>
        <div className="main">
          <div className="file_type">
            <span className="radio_name">File Type</span>
            <RadioGroup
              row
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              className="radio_group"
              value={value}
              onChange={handleChange}
            >
              <FormControlLabel
                value="image"
                control={<Radio />}
                label="Image"
              />
              <FormControlLabel
                value="video"
                control={<Radio />}
                label="Video"
              />
              <FormControlLabel
                value="reel"
                control={<Radio />}
                label="Reel"
              />
              <FormControlLabel
                value="thumbnail"
                control={<Radio />}
                label="Thumbnail"
              />
              <FormControlLabel
                value="audio"
                control={<Radio />}
                label="Audio (mp3)"
              />
            </RadioGroup>
          </div>
          <div className="files">
            {displayFiles.length > 0 && (
              <div className="file_preview">
                <CustomSwiper
                  data={displayFiles}
                  renderItem={(fileUrl, index) => (
                    <div key={index} className="file-preview-item">
                      {value === "image" || value === "thumbnail" ? (
                        <div className="image_container">
                          <img
                            src={fileUrl}
                            className="img_style"
                            alt={`selected file ${index + 1}`}
                          />
                        </div>
                      ) : value === "video" || value === "reel" ? (
                        <div className="video_container">
                          <video className="video_block" controls>
                            <source src={fileUrl} type="video/mp4" />
                          </video>
                        </div>
                      ) : (
                        <div className="video_container">
                          <audio src={fileUrl} controls />
                        </div>
                      )}
                    </div>
                  )}
                  centerInsufficientSlides={true}
                />
              </div>
            )}
            <FormControl>
              <input
                accept="image/*,video/*,audio/*"
                style={{ display: "none" }}
                id="img_file"
                type="file"
                name="img_file"
                ref={fileInputRef}
                onChange={(e) => handleUpload(e)}
                multiple
              />
              <label htmlFor="img_file">
                <CustomButton
                  variant="contained"
                  component="span"
                  startIcon={<CloudUploadOutlinedIcon />}
                  className="button_style"
                >
                  Upload {displayNameMapping[value]}
                </CustomButton>
              </label>
            </FormControl>
          </div>
          <div className="platform_check">
            {platform !== "facebook" ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkedPlatform.facebook}
                    onChange={(e) => handlePlatformUpdate(e, "facebook")}
                  />
                }
                label="Also upload on Facebook"
              />
            ) : (
              <></>
            )}
            {platform !== "instagram" ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkedPlatform.instagram}
                    onChange={(e) => handlePlatformUpdate(e, "instagram")}
                  />
                }
                label="Also upload on Instagram"
              />
            ) : (
              <></>
            )}
            {platform !== "youtube" ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkedPlatform.youtube}
                    onChange={(e) => handlePlatformUpdate(e, "youtube")}
                  />
                }
                label="Also upload on YouTube"
              />
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className="bottom_container">
          <CustomButton
            variant="text"
            className="btn_styl_close"
            onClick={handleClose}
          >
            Cancel
          </CustomButton>
          <CustomButton
            variant="contained"
            className="btn_styl"
            onClick={handleSubmit}
            disabled={selectedFiles.length === 0 ? true : false}
          >
            Add
          </CustomButton>
        </div>
      </Container>
    </Dialog>
  );

};

export default FileAttachmentsDialog;
